import { useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { ApiResourceTable } from "@project/admin/components/api-resource-table";
import { GaushalaAddDonationDialog } from "@project/admin/components/gaushala-add-donation-dialog";
import { GaushalaDonationDetailDialog } from "@project/admin/components/gaushala-donation-detail-dialog";
import type { Field } from "@project/admin/components/resource-table";
import { Button } from "@project/components/ui/button";
import { projectToast } from "@project/lib/toast";
import { generateReceiptPdf } from "@project/lib/receipt";
import { gaushalaDonationService } from "@project/services/admin/gaushala-donation.service";
import {
  GAUSHALA_DONATION_STATUS_OPTIONS,
  buildGaushalaDonationAddress,
  getGaushalaDonationAmount,
  getGaushalaDonationDonorName,
  getGaushalaDonationPaymentMode,
  getGaushalaDonationPhone,
  getGaushalaDonationReceiptNo,
  getGaushalaDonationSchemeLabel,
  normalizeGaushalaDonation,
  type GaushalaDonation,
} from "@project/types/gaushala";
import { Download, Eye, Plus } from "lucide-react";

const statusOptions = GAUSHALA_DONATION_STATUS_OPTIONS.map(option => ({
  value: option.value,
  label: option.label,
}));

const fields: Field[] = [
  {
    name: "donor_name",
    label: "Donor Name",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "email",
    label: "Email",
    type: "email",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "mobile_number",
    label: "Phone",
    type: "tel",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "preferred_tithi",
    label: "Preferred Tithi",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "scheme_title",
    label: "Scheme",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "amount",
    label: "Amount (₹)",
    type: "number",
    currency: true,
    showInTable: true,
    readOnly: true,
  },
  {
    name: "payment_mode",
    label: "Payment Mode",
    showInTable: true,
    readOnly: true,
  },
  {
    name: "mode",
    label: "Mode",
    type: "select",
    options: [
      { value: "online", label: "Online" },
      { value: "offline", label: "Offline" },
    ],
    showInTable: true,
    readOnly: true,
  },
  {
    name: "status",
    label: "Status",
    type: "select",
    options: statusOptions,
    showInTable: true,
    readOnly: true,
  },
  {
    name: "created_at",
    label: "Date",
    type: "date",
    showInTable: true,
    readOnly: true,
  },
];

function downloadReceipt(row: GaushalaDonation) {
  generateReceiptPdf({
    title: "Gaushala Donation Receipt",
    receiptNo: getGaushalaDonationReceiptNo(row),
    date: String(row.created_at ?? row.donation_date ?? ""),
    donationScheme: getGaushalaDonationSchemeLabel(row),
    donorName: getGaushalaDonationDonorName(row),
    donorMobile: getGaushalaDonationPhone(row),
    donorEmail: row.email ?? "",
    donorAddress: buildGaushalaDonationAddress(row),
    amount: getGaushalaDonationAmount(row),
    donationType: "Gaushala Seva",
    paymentMode: getGaushalaDonationPaymentMode(row),
    cashOrChequeNo: row.payments?.[0]?.razorpay_payment_id ?? "",
    occasion: row.occasion?.trim() ?? "",
    preferredTithi: row.preferred_tithi?.trim() ?? "",
  });
}

export default function GaushalaDonationsPage() {
  const [selectedDonation, setSelectedDonation] =
    useState<GaushalaDonation | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [listKey, setListKey] = useState(0);

  const handleViewDetails = async (row: GaushalaDonation) => {
    setSelectedDonation(null);
    setIsDetailsOpen(true);
    setLoadingDetails(true);
    try {
      const details = await gaushalaDonationService.get(row.id);
      setSelectedDonation(
        details
          ? normalizeGaushalaDonation(details)
          : normalizeGaushalaDonation(row)
      );
    } catch {
      setSelectedDonation(normalizeGaushalaDonation(row));
      projectToast.error("Could not load full donation details");
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <AdminShell
      title="Gaushala Donations"
      subtitle="Gau Seva donation records and offline entries"
    >
      <ApiResourceTable<GaushalaDonation>
        key={listKey}
        title="Gaushala Donation"
        labelField="donor_name"
        fields={fields}
        searchFields={[
          "donor_name",
          "email",
          "mobile_number",
          "preferred_tithi",
          "scheme_title",
          "display_name",
        ]}
        defaultValues={{}}
        statusFilterOptions={statusOptions}
        initialStatusFilter="success"
        csvFileName="gaushala-donations"
        toolbarActions={
          <Button
            type="button"
            size="sm"
            className="flex-1 gap-1 rounded-xl sm:flex-none"
            onClick={() => setIsAddOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Add Donation
          </Button>
        }
        list={async query => {
          const result = await gaushalaDonationService.listPage(query);
          return {
            ...result,
            data: result.data.map(normalizeGaushalaDonation),
          };
        }}
        renderRowActions={row => (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              title="View Details"
              onClick={() => void handleViewDetails(row)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5"
              title="Download Receipt"
              onClick={() => downloadReceipt(row)}
            >
              <Download className="h-4 w-4" />
              <span className="text-xs">Receipt</span>
            </Button>
          </div>
        )}
      />

      <GaushalaAddDonationDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        onSuccess={() => setListKey(key => key + 1)}
      />

      <GaushalaDonationDetailDialog
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        donation={selectedDonation}
        loading={loadingDetails}
        onDownloadReceipt={downloadReceipt}
      />
    </AdminShell>
  );
}
